import { call, put, all, takeEvery, select } from "redux-saga/effects";
import request from "utils/request";
import {
  fetchRequested,
  fetchSucceeded,
  fetchFailed,
  userLogout
} from "ducks/actions";
import {
  BASE,
  USER_LOGOUT_PATH,
  USER_SERVICE_REFRESH_TOKEN,
  ERROR_CODES,
  paths
} from "utils/constants";
import Alert from "react-s-alert";
import { handleAnalytics } from "analytics";
import {
  authTokenSelector,
  whiteListedErrorCodes,
  externalUserIdSelector,
  loginEmailSelector,
  makeSelectIsFetchingData,
  userEmailSelector,
  userSINSelector,
  orderRefSelector,
  localeSelector
} from "ducks/selectors";
import refreshToken from "./refreshTokenSaga";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import isPlainObject from "lodash/isPlainObject";
import fetchMedia from "./fetchMediaSaga";
import {
  api,
  isTokenInvalidError,
  isTokenRefreshRequired,
  validateSession,
  isWhiteListedError,
  getErrorMessageForCode,
  logoutUserCallback,
  isRetryableRecaptchaError,
  getErrorCodeMessage
} from "./utils";
import { getDeviceId } from "utils/localStorage";
import { gotoUrl } from "utils/helpers";
import { setNewRelicAttribute } from "utils/helpers";
import { languageAPIFormatCorrector } from "utils/languageAPIFormatCorrector";
import { appGlobalSettingsSelector } from "ducks/selectors/appGlobalSettings";
// import removePayment from "__mocks__/paymentMethods/removePayment.json";
// import setDefaultPaymentMethod from "__mocks__/paymentMethods/setDefaultPaymentMethod.json";
// import getDataForAddPaymentMethod from "__mocks__/paymentMethods/getDataForAddPaymentMethod.json";
// import paymentNotification from "__mocks__/notificationMethod/paymentNotification.json";
// import enLocalisationMock from "__mocks__/localisationMocks/en-CL.json";
// import jpLocalisationMock from "__mocks__/localisationMocks/ja-JP.json";
// import zhLocalisationMock from "__mocks__/localisationMocks/zh-TW.json";
// import urLocalisationMock from "__mocks__/localisationMocks/ur.json";
//import globalizationMethod from "__mocks__/globalizationMethod/globalization.json";
const TOKEN_REFRESH_MAX_RETRY_ATTEMPTS =
  process.env.REACT_APP_TOKEN_REFRESH_COUNT || 1;
const RECAPTCHA_TOKEN_MAX_RETRY_ATTEMPTS = 2;

export function* fetch({
  payload,
  retryAttempt = 0,
  retryRecaptchaAttempt = 0
}) {
  const {
    base: BASE_TYPE = BASE.OMS,
    key,
    path,
    headers,
    params,
    method,
    callback,
    captcha,
    override,
    isResendOtt,
    onErrorOverride,
    mediaStream = false,
    base64conversion,
    successData = {},
    version,
    guestSignUp,
    meta: metaData = {},
    whitelistedCodes = [],
    multiPartForm
  } = payload;

  // Get auth token from store if available
  const authToken = yield select(authTokenSelector);
  const loginEmail = yield select(loginEmailSelector);
  const userEmail = yield select(userEmailSelector);
  const email = userEmail || loginEmail;
  const orderRef = yield select(orderRefSelector);
  const userSIN = yield select(userSINSelector);
  const globalSettings = yield select(appGlobalSettingsSelector);
  const externalUserId = yield select(externalUserIdSelector);
  const whitelistedErrorCodesFromConfig = yield select(whiteListedErrorCodes);
  const whitelistedErrorCodesList = [
    ...whitelistedErrorCodesFromConfig,
    ...whitelistedCodes
  ];

  const { requestURL, requestWithCaptcha, options } = api()
    .baseFor(BASE_TYPE)
    .apiVersionFor(BASE_TYPE, version)
    .apiOptions(
      method,
      path,
      headers,
      params,
      authToken,
      metaData,
      multiPartForm
    )
    .build();
  // Handle mediastream type API requests
  if (mediaStream) {
    return yield call(
      fetchMedia,
      key,
      override,
      requestURL,
      options,
      callback,
      base64conversion
    );
  }

  try {
    setNewRelicAttribute("uniqueUserId", externalUserId, "hash");
    setNewRelicAttribute("orderRef", orderRef);
    setNewRelicAttribute("sin", userSIN);
    // can be used to extract email when email is unknown - but userId is available in newrelic results
    setNewRelicAttribute("userId", email, "rsa");
    // can be used to query when email is known
    setNewRelicAttribute("uniqueId", email, "hash");

    // if recaptcha is enabled for the API it will be executed with recaptcha token
    const makeRequest = captcha ? requestWithCaptcha : request;
    let response = yield call(
      fetchResponse,
      makeRequest,
      requestURL,
      options,
      payload,
      retryAttempt,
      retryRecaptchaAttempt
    );

    if (key === "appSettings") {
      if (response?.error?.action) {
        response = { ...response };
      } else {
        response.result.global_config = globalSettings;
      }
    }

    if (key === "validateGuestOtt" && response?.success) {
      response = {
        result: {
          accessToken: get(response, "result.accessToken"),
          numberLockStatus: get(response, "result.numberLockStatus"),
          requestId: get(response, "requestId")
        },
        requestId: get(response, "requestId"),
        success: true
      };
    }

    if (key === "locale") {
      if (options?.meta?.isBypass) {
        response = response?.result?.keys;
      } else {
        response =
          response.status === ERROR_CODES.rateLimitError
            ? response
            : languageAPIFormatCorrector(response?.result?.keys || []);
      }
    }

    // this is a small hack to ignore the failed fetchResponses due to token expiry
    if (!response) return;

    const fetchError = get(response, "error") || get(response, "failure");
    const { success, result, results, code } = response || {};
    const isSucess = success || code === 0 || !fetchError;

    if (isSucess) {
      let data = result || results || response || {};

      if (callback) callback(result || response);

      // merge successData only for plainObject response to avoid converting array to object
      if (isPlainObject(data)) {
        data = { ...data, ...successData };
      }
      if (guestSignUp) {
        data = { ...data, guestSignUp };
      }
      yield put(fetchSucceeded({ key, data, override, isResendOtt }));
    } else {
      yield* getErrorResponse(
        response,
        result,
        code,
        whitelistedErrorCodesList,
        onErrorOverride,
        callback,
        requestURL,
        key
      );
    }
  } catch (err) {
    const errorMessage = err?.message || err;
    const errorBody = err?.response || {};

    callback && callback({}, errorMessage, errorBody);

    const showErrorBanner = !isWhiteListedError(
      whitelistedErrorCodesList,
      errorBody
    );

    if (showErrorBanner && errorMessage) {
      Alert.error(errorMessage);
      // rollbar.error(errorMessage);
    }

    yield put(fetchFailed({ key, error: errorBody, onErrorOverride }));
  }
}

function* getErrorResponse(
  response,
  result,
  code,
  whitelistedErrorCodesList,
  onErrorOverride,
  callback,
  requestURL,
  key
) {
 
  let { error, errorReason, status, statusText, failure } = response || {};

  // Message obtained from result. This takes the highest priority
  const resultMessage = get(result, "message");
  // Error message obtained from error response
  const apiFailureMessage = yield getErrorMessageForCode(failure || error);
  const errorMessage =
    failure?.description ||
    error?.description ||
    error?.message ||
    resultMessage ||
    apiFailureMessage ||
    error;
  /**
   * Checks if the error code is whitelisted, because
   * at some cases it was needed to not to show error banner for some error codes.
   * The unique error codes are maintained in configuration manager.
   * However, there are situations we need to whitelist a HTTP error code just for one action. For this,
   * the action itself can provide error codes to whitelist.
   * (Provided via whitelistedCodes attribute from the action)
   */

  const showErrorBanner = !isWhiteListedError(
    whitelistedErrorCodesList,
    result || {
      errorId: Number(
        get(failure, "errorId") || get(failure, "code") || code || status
      )
    }
  );
  // Check if the response status is 429 (rate-limiting error)
  if (status === ERROR_CODES.rateLimitError) {
    const currentUrl = window.location.href;
    const errorValue = {
      redirectPath: currentUrl || "",
      message: JSON.parse(error.replaceAll("'", '"'))
    };
    yield put(
      fetchFailed({
        key: "rateLimitError",
        error: errorValue,
        onErrorOverride,
        failure
      })
    );
    gotoUrl(paths.ERROR_429);
    return;
  } else {
    if (isEmpty(error)) {
      error = errorMessage && [errorMessage];
    }

    if (showErrorBanner) {
      const errorCode = (failure || error, errorMessage || error);
      Alert.error(yield getErrorCodeMessage(errorCode));
    }
    if (callback) {
      const resultValue = result || {};
      const errorValue = failure || error;
      callback(resultValue, errorValue, status, statusText, key, requestURL);
    }
  }
  const errorId = get(failure, "errorId");
  if (errorId === ERROR_CODES.maximumOrderError) {
    yield put(
      fetchFailed({
        key: "ongoingOrder",
        error: get(failure, "errorCode"),
        errorReason,
        onErrorOverride,
        failure
      })
    );
    yield put(fetchSucceeded({ key: "order", data: {} }));
  } else {
    yield put(
      fetchFailed({ key, error, errorReason, onErrorOverride, failure })
    );
  }
}

/**
 * Fetches the response. If an exception occured, it will
 * build up an error object and return as response
 * @param {Function} makeRequest request function type
 * @param {string} requestURL request url
 * @param {object} options request options
 * @param {object} payload request payload
 */
export function* fetchResponse(
  makeRequest,
  requestURL,
  options,
  payload,
  retryAttempt,
  retryRecaptchaAttempt
) {
  let response = {};
  try {
    response = yield call(makeRequest, requestURL, options);
  } catch (err) {
    if (isRetryableRecaptchaError(err)) {
      const maxRetryRecaptchaReached =
        retryRecaptchaAttempt >= RECAPTCHA_TOKEN_MAX_RETRY_ATTEMPTS;
      if (!maxRetryRecaptchaReached) {
        return yield call(fetch, {
          payload: payload,
          retryAttempt: retryAttempt,
          retryRecaptchaAttempt: ++retryRecaptchaAttempt
        });
      }
    } else if (
      isTokenRefreshRequired(err) &&
      !requestURL.includes(USER_LOGOUT_PATH)
    ) {
      // Refresh token handling
      const maxRetryReached = retryAttempt >= TOKEN_REFRESH_MAX_RETRY_ATTEMPTS;
      if (maxRetryReached && requestURL.includes(USER_SERVICE_REFRESH_TOKEN)) {
        yield logoutUser();
        console.error(
          "Refresh token handling didnt succeed after max retry attempts. Redirecting to login page"
        );
      } else {
        const authHeader = "X-AUTH";
        const requestedToken = get(options, `headers.${authHeader}`);
        // expiredToken will have updated value if token was refreshed while the original request was in progress with requestedToken
        // we shouldn't use requestedToken for refresh, as it will return 401/403 in this case
        // use requestedToken only to compare with the newToken and retry the api call
        const expiredToken = yield select(authTokenSelector);
        yield call(
          refreshToken,
          expiredToken,
          ++retryAttempt,
          retryRecaptchaAttempt,
          requestURL
        );

        const newToken = yield select(authTokenSelector);
        console.log(
          new Date(),
          " : refreshToken(): Resume after successful refresh.",
          requestURL
        );

        // Some APIs can provide x-auth token as part of payload headers. If that token is invalid;
        // we do not want to resend the same bad token. Therefore we do a x-auth header cleanup before making the next retry
        const clonedHeaders = {
          ...payload?.headers
        };
        // telling API builder to fill the x-auth with the latest available
        delete clonedHeaders[authHeader];
        const updatedPayload = {
          ...payload,
          headers: clonedHeaders
        };

        // this will basically avoid calling the same api again if token has not changed
        if (newToken && newToken !== requestedToken) {
          return yield call(fetch, {
            payload: updatedPayload,
            retryAttempt: ++retryAttempt,
            retryRecaptchaAttempt
          });
        }
      }
    }

    // Refresh token error handling (Can happen due to: Invalid/Incorrect auth-token)
    if (isTokenInvalidError(err)) {
      const locale = yield select(localeSelector);
      Alert.error(
        get(locale, "logout_message", "You are logged out, please log in again")
      );

      return logoutUserCallback();
    }

    const errorData = err?.response || {};

    response = {
      ...errorData,
      error: errorData.message || errorData?.error || err,
      errorReason: errorData.message_code,
      status: err.status,
      statusText: err.statusText
    };
  }

  return response;
}

export function* logoutUser() {
  const headers = {};
  const externalId = yield select(externalUserIdSelector);
  const params = {
    device_id: getDeviceId(),
    user_id: externalId,
    device_type: "web",
    token_expiry: true
  };

  const logoutInProgress = yield select(makeSelectIsFetchingData("userLogout"));
  if (!logoutInProgress) {
    const FORCE_LOGOUT_MESSAGE = yield getErrorMessageForCode({
      title: "logout_message"
    });
    Alert.error(FORCE_LOGOUT_MESSAGE);
    return yield put(userLogout(headers, params, logoutUserCallback));
  }
}

export default function* rootSaga() {
  yield takeEvery("*", handleAnalytics);
  yield takeEvery("*", validateSession);
  // yield takeEvery("*", validateNumberLockSession);
  yield takeEvery("REFRESH_TOKEN", refreshToken);
  yield all([takeEvery(fetchRequested, fetch)]);
}
